import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Header,
  Modal,
  Form,
  Segment,
} from 'semantic-ui-react';
import moment from 'moment-timezone';
import { required, phoneNumber, email as validateEmail } from '@mhub/web-validations';

import * as endpoints from '../../constants/apiEndpoints';
import { flashSuccessMessage, flashErrorMessage } from '../../actions/flash';
import AppForm from '../../components/AppForm';
import PhoneInput from '../../components/AppForm/PhoneInput';
import INTL_CALL_CODES from '../../constants/intlCallCodes';
import { fetchAPI } from '../../actions/fetchM';
import { isForeignCompany } from '../../utils/companies';
import INTL_FLAG_CODES from '../../constants/intlFlagCodes';

const actionStyle = { display: 'flex', justifyContent: 'center' };

export default class InviteLawyerForm extends Component {
  static contextTypes = {
    store: PropTypes.object,
  }
  static propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    onConfirm: PropTypes.func,
    projectName: PropTypes.string,
    loanTrackerProjectID: PropTypes.number.isRequired,
    loading: PropTypes.bool,
    setLoading: PropTypes.func,
    company: PropTypes.shape({
      name: PropTypes.string,
      countryCode: PropTypes.string,
    }),
  }
  constructor(props) {
    super(props);
    const company = props.company;
    let defaultCountryPhoneCode = '60';
    if (isForeignCompany(company)) {
      const idx = INTL_FLAG_CODES.findIndex(f => f.countryCode === company.countryCode);
      if (idx !== -1) {
        defaultCountryPhoneCode = INTL_FLAG_CODES[idx].value;
      }
    }
    this.state = {
      mobilePrefix: defaultCountryPhoneCode,
      mobile: '',
      name: '',
      email: '',
      firm: '',
      errMessages: [],
      invitation: null,
      isInvited: false,
      isExisted: false,
      isPanel: false,
      company: {},
    };
    this.validation = {
      name: createRef(),
      email: createRef(),
      mobilePrefix: createRef(),
      mobile: createRef(),
      firm: createRef(),
    };
  }
  setExternalLoading = (loading = false) => {
    const { setLoading } = this.props;
    if (setLoading) {
      setLoading(loading);
    }
  }
  setComponentState = (state = {}) => new Promise(res => this.setState(state, () => res(state)))
  inviteLawyer = async (body) => {
    const { store } = this.context;
    const { errMessages } = this.state;
    if (errMessages.length > 0) {
      store.dispatch(flashErrorMessage({ header: 'Please fix form errors', list: errMessages.map(l => l.message) }));
      return;
    }
    const { loanTrackerProjectID } = this.props;
    if (!loanTrackerProjectID) {
      await store.dispatch(flashErrorMessage({ message: 'This project is not linked in loan tracker.' }));
      return;
    }
    if (this.form) {
      await this.form.setLoading(true);
    }
    let res;
    try {
      res = await store.dispatch(fetchAPI('lawyerAPI', endpoints.INVITE_LAWYER(loanTrackerProjectID), body, 'post', {
        'X-Api-Token': process.env.LAWYER_API_TOKEN,
      }));
    } catch (e) {
      if (this.form) {
        await this.form.setLoading(false);
      }
      if (e && e.response && e.response.data) {
        const { error_message, error_data } = e.response.data;
        let isInvited = false;
        let isExisted = false;
        let isPanel = false;
        let invitation;
        if (error_message.match(/already invited/i)) {
          invitation = error_data;
          isInvited = true;
          if (invitation) {
            isExisted = invitation.lawyer_id && invitation.lawyer_id !== '0';
          }
        }
        if (error_message.match(/already exists/i)) {
          isExisted = true;
          if (error_message.match(/Lawyer already exists/i)) {
            invitation = error_data;
            isInvited = true;
            isPanel = error_data.panels.split(',').some(p => `${p}` === `${loanTrackerProjectID}`);
          } else {
            const name = error_data.Attributes.find(f => f.Name === 'name').Value;
            invitation = {
              name,
              email: body.email,
              lawyer_id: error_data.Username,
            };
          }
        }
        if (invitation) {
          await this.setComponentState({ invitation, isExisted, isInvited, isPanel });
        }
        return;
      }
      Bugsnag.notifyException(e);
      await store.dispatch(flashErrorMessage({ message: 'An error occurred, please refresh page' }));
      return;
    }

    if (res) {
      await this.setComponentState({ invitation: res });
      await store.dispatch(flashSuccessMessage({ header: 'Invite success', message: `Lawyer ${res.name} is successfully invited.` }));
    }

    if (this.form) {
      await this.form.setLoading(false);
    }
    if (this.props.onConfirm) {
      this.props.onConfirm();
    }
  }
  addPanelLawyer = async (lawyerID) => {
    await this.setExternalLoading(true);
    const { store } = this.context;
    const { loanTrackerProjectID, projectName } = this.props;
    if (!loanTrackerProjectID) {
      await store.dispatch(flashErrorMessage({ message: 'This project is not linked in loan tracker.' }));
      return;
    }
    const body = {
      lawyer_ids: [lawyerID],
      project_name: projectName,
    };
    try {
      await store.dispatch(fetchAPI('lawyerAPI', endpoints.PANEL_LAWYERS(loanTrackerProjectID), body, 'put', {
        'X-Api-Token': process.env.LAWYER_API_TOKEN,
      }));
    } catch (e) {
      Bugsnag.notifyException(e);
      await store.dispatch(flashErrorMessage({ message: 'An error occurred, please refresh page' }));
      await this.setExternalLoading(false);
      return;
    }

    await store.dispatch(flashSuccessMessage({ header: 'Add Success', message: 'Panel lawyer(s) successfully added.' }));
    await this.setExternalLoading(false);
    if (this.props.onConfirm) {
      this.props.onConfirm();
    }
  }
  resetData = (cb = () => {}) => {
    this.setState({
      mobilePrefix: '60',
      mobile: '',
      name: '',
      email: '',
      firm: '',
      errMessages: [],
      invitation: null,
    }, cb);
  }
  handleSubmit = (event) => {
    event.preventDefault();
    const { name, email, firm, mobile, mobilePrefix } = this.state;
    const { company = {} } = this.props;
    const companyName = company.name;
    const data = {
      name,
      email,
      firm,
      mobile,
      mobilePrefix,
    };
    return AppForm.validate(this.validation, data)
      .then((validations) => {
        this.setState({
          errMessages: validations.filter(v => !v.isValid),
        }, () => {
          const lawyer = {
            name,
            email: email.toLowerCase(),
            firm,
            mobile_prefix: mobilePrefix,
            mobile,
            company_name: companyName,
          };
          this.inviteLawyer(lawyer);
        });
      });
  }
  handleOnClose = (e) => {
    if (this.props.onClose) {
      this.props.onClose(e);
    }
  }
  handleOnConfirm = (e) => {
    if (this.props.onConfirm) {
      this.props.onConfirm(e);
    }
  }
  handleOnSubmit = (e) => {
    e.preventDefault();
    this.form.handleSubmit(e);
  }
  handleOnChange = (e, { name, value }) => {
    this.setState({ [name]: value });
  }
  handleAddPanel = (e) => {
    e.preventDefault();
    this.addPanelLawyer(this.state.invitation.lawyer_id);
  }
  renderContent = () => {
    const { invitation, isInvited, isExisted, isPanel } = this.state;
    if (invitation) {
      if (isExisted) {
        return (
          <Modal.Content>
            <Segment basic textAlign="center">
              <p>There is an existing account found with the email address <strong>{invitation.email}</strong>.</p>
              {isInvited && !isPanel ? <p>Do you want to add <strong>{invitation.name} ({invitation.email})</strong> to Panel Lawyer?</p> : null}
              {isPanel ? <p>Lawyer is already added as Panel Lawyer.</p> : null}
            </Segment>
          </Modal.Content>
        );
      }
      return (
        <Modal.Content>
          <Segment basic textAlign="center">
            <p>An activation email has been sent to <strong>{invitation.name} ({invitation.email})</strong>{isInvited && invitation.created_at ? ` on ${moment(invitation.created_at).format('YYYY-MM-DD')}` : ''}.</p>
            <p>You may assign your case(s) to the lawyer once his/her account is registered and activated.</p>
          </Segment>
        </Modal.Content>
      );
    }
    return this.renderForm();
  }
  renderForm = () => (
    <Modal.Content>
      <AppForm
        onSubmit={this.handleSubmit}
        error={this.state.errMessages.length > 0}
        ref={(ref) => { this.form = ref; }}
      >
        <AppForm.Input
          autoComplete="off"
          name="name"
          onChange={this.handleOnChange}
          label="Name"
          ref={this.validation.name}
          validation={[required]}
          value={this.state.name}
        />
        <AppForm.Input
          autoComplete="off"
          name="email"
          onChange={this.handleOnChange}
          label="Email Address"
          ref={this.validation.email}
          validation={[required, validateEmail]}
          value={this.state.email}
        />
        <Form.Group widths="equal">
          <AppForm.Validate
            validation={[required]}
            ref={this.validation.mobilePrefix}
            onChange={this.handleOnChange}
            validateLabel="Mobile prefix"
          >
            <Form.Select
              name="mobilePrefix"
              onChange={this.handleOnChange}
              options={INTL_CALL_CODES}
              label="Mobile Prefix"
              search
              selectOnBlur={false}
              value={this.state.mobilePrefix}
            />
          </AppForm.Validate>
          <PhoneInput
            autoComplete="off"
            name="mobile"
            onChange={this.handleOnChange}
            placeholder={this.state.mobilePrefix === '60' ? 'Mobile* (e.g. 196756118)' : 'Mobile*'}
            label={this.state.mobilePrefix === '60' ? 'Mobile* (e.g. 196756118)' : 'Mobile*'}
            prefix={this.state.mobilePrefix}
            ref={this.validation.mobile}
            type="tel"
            validation={[required, {
              validate: phoneNumber,
              option: this.state.mobilePrefix === 'other' ? {} : {
                isMobile: true,
                phonePrefix: this.state.mobilePrefix,
              },
            }]}
            value={this.state.mobile}
            validateLabel="Mobile"
          />
        </Form.Group>
        <AppForm.Input
          autoComplete="off"
          name="firm"
          onChange={this.handleOnChange}
          label="Law Firm Name"
          ref={this.validation.firm}
          validation={[required]}
          value={this.state.firm}
        />
      </AppForm>
    </Modal.Content>
  )
  renderAction = () => {
    const { invitation, isExisted, isInvited, isPanel } = this.state;
    const { loading } = this.props;
    if (invitation) {
      if (isExisted && isInvited && !isPanel) {
        return (
          <Modal.Actions style={actionStyle}>
            <Button basic negative onClick={this.handleOnClose} icon="cancel" content="No" inverted disabled={loading} />
            <Button basic positive onClick={this.handleAddPanel} icon="checkmark" content="Yes" inverted disabled={loading} />
          </Modal.Actions>
        );
      }
      return (
        <Modal.Actions style={actionStyle}>
          <Button
            primary
            onClick={this.handleOnClose}
            content="OK"
            disabled={loading}
          />
        </Modal.Actions>
      );
    }
    return (
      <Modal.Actions>
        <Button basic negative onClick={this.handleOnClose} icon="cancel" content="Cancel" inverted disabled={loading} />
        <Button basic positive onClick={this.handleOnSubmit} icon="checkmark" content="Invite" inverted disabled={loading} />
      </Modal.Actions>
    );
  }
  render() {
    const { invitation, isExisted, isInvited } = this.state;
    return (
      <Modal
        size={invitation ? 'small' : null}
        open={this.props.open}
        onClose={this.handleOnClose}
        closeIcon="close"
      >
        <Header
          content={(isExisted || isInvited) ? 'Account Found' : 'Invite Lawyer'}
          subheader={(isExisted || isInvited) ? null : 'Please fill in lawyer\'s detail.'}
        />
        {this.renderContent()}
        {this.renderAction()}
      </Modal>
    );
  }
}



// WEBPACK FOOTER //
// ./src/containers/modals/InviteLawyerForm.jsx